import { faEyeSlash, faPen, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Radio } from '@sorare/core/src/atoms/inputs/Radio';
import { SearchInput } from '@sorare/core/src/atoms/inputs/SearchInput';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BodyM, HeadlineXS, LabelS } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { Warning } from '@sorare/core/src/contexts/intl/Warning';
import { useDecks } from '@sorare/core/src/hooks/decks/useDecks';
import { glossary } from '@sorare/core/src/lib/glossary';

const Body = styled(Vertical)`
  padding: var(--double-unit);
`;
const StyledHorizontal = styled(SBHorizontal)`
  cursor: pointer;
  padding: var(--unit) 0;
`;
const Footer = styled(Horizontal).attrs({ gap: 2, center: true })`
  padding: var(--double-unit);
`;

type Props = {
  highlightedDeckId?: string;
  sport: Sport;
  open: boolean;
  onClose: () => void;
  onSubmit: (slug: string) => void;
  onAddDeck: () => void;
  onEditDeck: (slug: string) => void;
};

export const ChooseDeck = ({
  highlightedDeckId,
  sport,
  open,
  onClose,
  onSubmit,
  onAddDeck,
  onEditDeck,
}: Props) => {
  const { formatMessage } = useIntl();
  const [query, setQuery] = useState<string>();
  const [selectedDeckId, setSelectedDeckId] = useState<string | undefined>(
    highlightedDeckId
  );

  const { decks, loading } = useDecks({
    sport,
    query,
  });

  const selectedDeck = useMemo(
    () => decks.find(({ id }) => id === selectedDeckId),
    [decks, selectedDeckId]
  );

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={onClose}
      title={
        <HeadlineXS as="h6">
          <FormattedMessage
            id="ChooseDeck.selectInYourLists"
            defaultMessage="Select in your lists below"
          />
        </HeadlineXS>
      }
      footer={
        <Footer>
          <Button
            startIcon={<FontAwesomeIcon icon={faPlus} />}
            color="tertiary"
            size="medium"
            fullWidth
            onClick={onAddDeck}
          >
            <FormattedMessage
              id="AddCardToDeck.newList"
              defaultMessage="New list"
            />
          </Button>
          <Button
            color="primary"
            size="medium"
            fullWidth
            disabled={!selectedDeckId || !selectedDeck?.visible}
            onClick={() => {
              if (selectedDeckId) {
                onSubmit(selectedDeckId);
                onClose();
              }
            }}
          >
            <FormattedMessage {...glossary.confirm} />
          </Button>
        </Footer>
      }
    >
      <Body>
        <SearchInput
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={formatMessage(glossary.search)}
        />
        {loading && !decks?.length ? (
          <LoadingIndicator small />
        ) : (
          <>
            {!decks.length && (
              <Warning variant="yellow">
                <FormattedMessage
                  id="Profile.HallOfFame.ChooseDeck.noListFound"
                  defaultMessage="No list found"
                />
              </Warning>
            )}
            {decks.map(deck => (
              <StyledHorizontal
                role="button"
                onClick={() => deck.visible && setSelectedDeckId(deck.id)}
                key={deck.slug}
              >
                <Horizontal>
                  <BodyM>{deck.name} </BodyM>
                  {!deck.visible && (
                    <Tooltip
                      title={
                        <FormattedMessage
                          id="Profile.HallOfFame.ChooseDeck.privateListsCanNotBeHighlight"
                          defaultMessage="Private lists cannot be highlighted"
                        />
                      }
                    >
                      <FontAwesomeIcon
                        icon={faEyeSlash}
                        color="var(--c-red-400)"
                      />
                    </Tooltip>
                  )}
                </Horizontal>
                <Horizontal gap={2}>
                  <IconButton
                    small
                    color="transparent"
                    icon={faPen}
                    onClick={() => onEditDeck(deck.slug)}
                  />
                  <LabelS>{deck.tokensCount}</LabelS>
                  <Radio
                    name={deck.name}
                    disabled={!deck.visible}
                    labelContent={null}
                    checkedColor="var(--c-blue-400)"
                    value={deck.id}
                    checked={selectedDeckId === deck.id}
                    onChange={e => {
                      if (e.target.checked) setSelectedDeckId(deck.id);
                    }}
                  />
                </Horizontal>
              </StyledHorizontal>
            ))}
          </>
        )}
      </Body>
    </Dialog>
  );
};
